import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { gexService } from '../../services/gexService';
import type { OHLCBar } from '../../types/tdSequential';
import { EXPLORER_CACHE } from './cachePolicy';

/**
 * 252 sessions ≈ 52 weeks. The window is sized by the widest consumer — the
 * 52-week high/low — rather than by the chart, which displays a slice of it.
 * One request either way.
 */
export const DAILY_SESSIONS = 252;

/** True when the request failed with a 404, false for anything else. */
export function is404(err: unknown): boolean {
  return (err as { response?: { status?: number } } | null)?.response?.status === 404;
}

export interface DailyBars {
  /** Ascending by date, deduplicated, every price finite. Possibly empty. */
  bars: OHLCBar[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Daily OHLC for a symbol, sanitised.
 *
 * Shared by the Overview chart and the hero bar's 5-day change so they issue
 * one request between them. Keeping the query options in one place also stops
 * the two from disagreeing — react-query resolves options from whichever
 * observer mounts first, which is a subtle way to get inconsistent retries.
 *
 * lightweight-charts asserts on its input rather than tolerating it: a null
 * price, a duplicate date or a series that is not strictly ascending by time
 * all throw out of setData. Feeds vary in how carefully they order and fill
 * daily bars, so the data is put in order here rather than trusted.
 */
export function useDailyBars(symbol: string): DailyBars {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['overviewOhlc', symbol, DAILY_SESSIONS],
    queryFn: () => gexService.fetchOHLC(symbol, DAILY_SESSIONS),
    // A symbol outside the dataset comes back as an empty list, not an error.
    retry: 2,
    ...EXPLORER_CACHE,
    enabled: !!symbol,
  });

  const bars = useMemo(() => {
    const byDate = new Map<string, OHLCBar>();

    (Array.isArray(data) ? data : []).forEach((bar) => {
      if (!bar?.timestamp) return;
      const ohlc = [bar.open, bar.high, bar.low, bar.close];
      if (!ohlc.every((v) => typeof v === 'number' && Number.isFinite(v))) return;
      // Later rows win: a corrected bar supersedes the original.
      byDate.set(bar.timestamp.split('T')[0], bar);
    });

    // ISO dates sort correctly as plain strings — no parsing needed.
    return [...byDate.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, bar]) => bar);
  }, [data]);

  return { bars, isLoading, isError };
}

export interface YearRange {
  low: number;
  high: number;
  /** Where `price` sits across the range, 0–100. */
  pct: number;
  /** Percentage below the 52-week high. Zero or negative. */
  fromHighPct: number;
}

/**
 * 52-week high/low from intraday extremes, and where a price sits between
 * them.
 *
 * Uses each bar's high and low rather than its close — the 52-week high is
 * the highest price traded, not the highest close, and quoting the latter
 * would disagree with every other source the user checks.
 */
export function yearRange(bars: OHLCBar[], price: number | null | undefined): YearRange | null {
  if (!bars.length) return null;

  let low = Infinity;
  let high = -Infinity;
  for (const bar of bars) {
    if (bar.low < low) low = bar.low;
    if (bar.high > high) high = bar.high;
  }
  if (!Number.isFinite(low) || !Number.isFinite(high)) return null;

  const current = price ?? bars[bars.length - 1].close;
  const span = high - low;
  // A flat year would divide by zero; treat it as sitting at the top.
  const pct = span > 0 ? ((current - low) / span) * 100 : 100;
  const fromHighPct = high > 0 ? (current / high - 1) * 100 : 0;

  return { low, high, pct: Math.min(100, Math.max(0, pct)), fromHighPct };
}
